"use client";

import { useEffect, useState, type FormEvent } from "react";
import { X } from "lucide-react";
import { buscarJsonComCache, invalidarCacheCliente } from "@/lib/fetch-cache-cliente";
import type { ContextoMeta } from "@/lib/followzap-navigation";
import { Button } from "./Button";
import { Dialog } from "./Dialog";
import { useExperienciaProduto } from "./ExperienciaProvider";
import { Input } from "./Input";
import { LoadingIndicator } from "./LoadingIndicator";

type Metas = Partial<Record<ContextoMeta, number>>;
type Rascunhos = Partial<Record<ContextoMeta, number | "">>;
type RespostaMeta = { usuarioId?: string | null; papel?: string; meta_mensal: number };
type RespostaSalvar = { ok?: boolean; valor?: number; erro?: string };

function enderecoMeta(contexto: ContextoMeta) {
  return contexto === "gestao" ? "/api/configuracoes?contexto=gestao" : "/api/configuracoes";
}

export function PopupMeta({ contextos, onClose }: { contextos: readonly ContextoMeta[]; onClose: () => void }) {
  const { dados } = useExperienciaProduto();
  const [metas, setMetas] = useState<Metas | null>(null);
  const [rascunhos, setRascunhos] = useState<Rascunhos>({});
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState<ContextoMeta | null>(null);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  useEffect(() => {
    if (!dados?.id || contextos.length === 0) return;
    let ativo = true;
    Promise.all(contextos.map(async (contexto) => {
      const resposta = await buscarJsonComCache<RespostaMeta>(enderecoMeta(contexto), { force: true });
      if (resposta.usuarioId !== dados.id || resposta.papel !== dados.papel || !Number.isFinite(resposta.meta_mensal)) {
        throw new Error("Contexto da Meta divergente.");
      }
      return [contexto, resposta.meta_mensal] as const;
    }))
      .then((resultados) => {
        if (!ativo) return;
        const valores = Object.fromEntries(resultados) as Metas;
        setMetas(valores);
        setRascunhos(valores);
      })
      .catch((falha) => {
        if (ativo) setErro(falha instanceof Error ? falha.message : "Não foi possível carregar a Meta.");
      })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [dados?.id, dados?.papel, contextos]);

  async function salvar(evento: FormEvent<HTMLFormElement>, contexto: ContextoMeta) {
    evento.preventDefault();
    if (salvando) return;
    const valor = rascunhos[contexto];
    if (typeof valor !== "number" || !Number.isInteger(valor) || valor < 1) {
      setErro("Informe um número inteiro positivo.");
      return;
    }
    setSalvando(contexto);
    setErro("");
    setSucesso("");
    try {
      const resposta = await fetch("/api/configuracoes", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chave: "meta_mensal", valor, contexto }),
      });
      const resultado = await resposta.json().catch(() => ({})) as RespostaSalvar;
      if (!resposta.ok || resultado.ok !== true || typeof resultado.valor !== "number") {
        throw new Error(resultado.erro ?? "Não foi possível salvar a Meta.");
      }
      setMetas((atuais) => ({ ...atuais, [contexto]: resultado.valor }));
      setRascunhos((atuais) => ({ ...atuais, [contexto]: resultado.valor }));
      invalidarCacheCliente("/api/configuracoes");
      invalidarCacheCliente(contexto === "gestao" ? "/api/gestor/resumo" : "/api/vendas");
      setSucesso(contexto === "gestao" ? "Meta de gestão salva." : "Meta individual salva.");
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : "Não foi possível salvar a Meta.");
    } finally {
      setSalvando(null);
    }
  }

  if (!dados?.id || contextos.length === 0) return null;

  return (
    <Dialog
      open
      onOpenChange={(aberto) => { if (!aberto && !salvando) onClose(); }}
      closeOnEscape={!salvando}
      closeOnBackdrop={!salvando}
      aria-labelledby="titulo-popup-meta"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 id="titulo-popup-meta" className="text-xl font-semibold">Meta</h2>
        <Button variant="ghost" size="icon" aria-label="Fechar Meta" onClick={onClose} disabled={!!salvando}><X className="h-5 w-5" /></Button>
      </div>

      {carregando ? <LoadingIndicator className="py-8">Carregando Meta...</LoadingIndicator> : metas ? (
        <div className="mt-5 space-y-5">
          {contextos.map((contexto) => (
            <form key={contexto} onSubmit={(evento) => void salvar(evento, contexto)} className="space-y-3 rounded-[var(--fz-field-radius)] border border-[var(--fz-field-border)] p-4">
              <h3 className="font-semibold">{contexto === "gestao" ? "Meta de gestão" : "Meta individual"}</h3>
              <p className="text-sm">Meta atual: {metas[contexto]?.toLocaleString("pt-BR") ?? "—"}</p>
              <label className="block space-y-1.5 text-sm font-medium">
                Meta mensal de vendas
                <Input
                  type="number"
                  min={1}
                  step={1}
                  value={rascunhos[contexto] ?? ""}
                  onChange={(evento) => setRascunhos((atuais) => ({ ...atuais, [contexto]: evento.target.value === "" ? "" : Number(evento.target.value) }))}
                  disabled={!!salvando}
                  required
                />
              </label>
              <Button type="submit" disabled={!!salvando}>{salvando === contexto ? "Salvando..." : "Salvar meta"}</Button>
            </form>
          ))}
        </div>
      ) : null}
      {erro ? <p role="alert" className="mt-4 text-sm text-[var(--fz-field-error)]">{erro}</p> : null}
      {sucesso ? <p role="status" className="mt-4 text-sm">{sucesso}</p> : null}
    </Dialog>
  );
}
